import DeleteIcon from '@mui/icons-material/Delete'
import {SessionItemData} from './Sessions'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemButton,
  Typography
} from '@mui/material'
import {useRef, useState} from 'react'
import {format} from 'date-fns'
import {useTranslation} from 'react-i18next'

interface SessionItemProps {
  session: SessionItemData
  selected: boolean
  onSelectSession: (item: SessionItemData) => void
  onDeleteSession?: (item: SessionItemData) => void
}

const SessionItem = ({
  session,
  selected,
  onSelectSession,
  onDeleteSession
}: SessionItemProps) => {
  const [t] = useTranslation()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [pendingDeleteSession, setPendingDeleteSession] =
    useState<SessionItemData | null>(null)

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const longPressTriggered = useRef(false)
  const longPressDelay = 600

  const startY = useRef(0)
  const startX = useRef(0)
  const isScrolling = useRef(false)

  const startPressTimer = () => {
    longPressTriggered.current = false
    timerRef.current = setTimeout(() => {
      if (!isScrolling.current) {
        longPressTriggered.current = true
        setPendingDeleteSession(session)
        setOpenDeleteDialog(true)
      }
    }, longPressDelay)
  }

  const clearPressTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (session.demandId) return

    const touch = e.touches[0]
    startX.current = touch.clientX
    startY.current = touch.clientY
    isScrolling.current = false
    startPressTimer()
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    const dx = Math.abs(touch.clientX - startX.current)
    const dy = Math.abs(touch.clientY - startY.current)

    if (dx > 10 || dy > 10) {
      isScrolling.current = true
      clearPressTimer()
    }
  }

  const handleTouchEnd = () => {
    clearPressTimer()
    if (!longPressTriggered.current && !isScrolling.current) {
      onSelectSession(session)
    }
  }

  const handleMouseUp = () => {
    clearPressTimer()
    if (!longPressTriggered.current) {
      onSelectSession(session)
    }
  }

  const handleDeleteSession = () => {
    if (onDeleteSession && pendingDeleteSession) {
      onDeleteSession(pendingDeleteSession)
    }
    setOpenDeleteDialog(false)
    setPendingDeleteSession(null)
  }

  return (
    <>
      <ListItemButton
        onMouseDown={!session.demandId ? startPressTimer : undefined}
        onMouseUp={
          !session.demandId ? handleMouseUp : () => onSelectSession(session)
        }
        onMouseLeave={!session.demandId ? clearPressTimer : undefined}
        onTouchStart={!session.demandId ? handleTouchStart : undefined}
        onTouchMove={!session.demandId ? handleTouchMove : undefined}
        onTouchEnd={!session.demandId ? handleTouchEnd : undefined}
        sx={(theme) => ({
          height: 42,
          borderRadius: 3,
          boxShadow: theme.shadows[8],
          display: 'flex',
          marginY: 0.5,
          marginRight: {xs: 2, md: 0},
          justifyContent: 'space-between',
          alignItems: 'center',
          color: selected
            ? theme.palette.common.white
            : theme.palette.common.black,
          backgroundColor: selected
            ? theme.palette.primary.main
            : 'transparent',
          '&:hover': {
            backgroundColor: selected
              ? theme.palette.primary.dark
              : theme.palette.action.hover,
            color: selected
              ? theme.palette.common.white
              : theme.palette.common.black
          },
          '&:hover .delete-icon': {
            visibility: 'visible',
            color: selected
              ? theme.palette.common.white
              : theme.palette.grey[600]
          }
        })}
      >
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'clip',
            flexGrow: 1,
            maskImage: 'linear-gradient(to right, black 80%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, black 80%, transparent)'
          }}
        >
          {session.topic
            ? session.topic
            : format(new Date(session.lastMessageDateCreated), 'PP, HH:mm')}
        </Typography>
        {!session.demandId && (
          <IconButton
            size="small"
            className="delete-icon"
            sx={{visibility: 'hidden'}}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              setPendingDeleteSession(session)
              setOpenDeleteDialog(true)
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </ListItemButton>

      {/* Delete confirmation dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>
          {t('fnd-assistant-dialog-delete-session-title')}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <DialogContentText>
            {t('fnd-assistant-dialog-delete-session-subtitle')}{' '}
            <Typography component="span" fontWeight="bold">
              {session.topic
                ? session.topic
                : format(
                    new Date(session.lastMessageDateCreated),
                    'PP, hh:mm a'
                  )}
              ?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            size="large"
            onClick={() => setOpenDeleteDialog(false)}
          >
            {t('fnd-assistant-common-cancel')}
          </Button>
          <Button color="error" onClick={handleDeleteSession}>
            {t('fnd-assistant-common-delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SessionItem
